/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, February 22nd 2022, 6:05:53 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useTranslatedText } from "core";
import { APISERVICEPATHS } from "lib/api";
import { getFormConfiguration } from "lib/factories";
import { ListEditorProvider } from "lib/providers";
import { FormConfiguration } from "lib/schemas";
import { FormSimpleListEditor } from "form-builder/components";
import { useServiceBuilderContext } from "service-builder/providers";

export const SubmissionFormsPage = () => {
  const LANG = useTranslatedText();
  const {
    processDefinitionContext: { currentProcessDefinition },
    removeFormFromProcessDefinition,
    updateTemplateArtifactsOnAPISaveFormConfiguration,
  } = useServiceBuilderContext();

  const onRemoveForm = React.useCallback(
    (form: FormConfiguration) => {
      removeFormFromProcessDefinition(form, "application_form");
    },
    [removeFormFromProcessDefinition]
  );

  const onSaveFormInfo = React.useCallback(
    (updatedFormConfig: FormConfiguration) => {
      updateTemplateArtifactsOnAPISaveFormConfiguration(updatedFormConfig, "application_form"); // Update the form type on the artifact as per the form type selected in the props, since the may need to treat the form differently from its actual form type.
    },
    [updateTemplateArtifactsOnAPISaveFormConfiguration]
  );

  const defaultNewFormConfiguration = getFormConfiguration(currentProcessDefinition.artifact_org, {
    form_type: "application_form",
    title: "",
    login_required: true,
    sites: [currentProcessDefinition.site],
    entry_number_default_prefix: currentProcessDefinition.service_code,
  });

  return (
    <ListEditorProvider<FormConfiguration, false>
      uniqueIdentifier="id"
      apiServicePath={APISERVICEPATHS.processDefinitions}
      apiPathParams={[currentProcessDefinition.id, "submission_forms"]}
      dependencyList={[currentProcessDefinition.id]}
    >
      <div className="os-service-builder-card">
        <FormSimpleListEditor
          popUpTitle="Submission Form Info"
          popUpDescription={
            <>
              <p>{LANG.serviceBuilderSubmissionFormDescription}</p>
              <p>{LANG.serviceBuilderFormInstructions}</p>
            </>
          }
          noDataMessage="There are no submission forms configured for this service. You need to add at least one submission form before deploying your service."
          form_type="application_form"
          allowedFormTypes={["application_form", "registration_form"]}
          onRemoveForm={onRemoveForm}
          onSaveFormInfo={onSaveFormInfo}
          defaultNewFormConfiguration={defaultNewFormConfiguration}
        />
      </div>
    </ListEditorProvider>
  );
};
